/**
 * WeeklyFocusPicker — the shared "pick this week's focus virtue" UI used by
 * both the Daily Check-in (when the new week has no virtue yet) and the
 * Dashboard (the client's "change it" affordance). Presentational + save:
 * the parent fetches `virtues` + `current` and passes them in; the picker
 * owns the selection/reason state and the `setWeeklyFocus` call, then hands
 * the saved entry back via `onSaved`.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import type { AffirmationGroupEntry, WeeklyFocusEntry } from '@/lib/api-client';

type Axis = 'energy' | 'work' | 'love';
const AXES: readonly Axis[] = ['energy', 'work', 'love'];

interface WeeklyFocusPickerProps {
  readonly clientId: string;
  /** ISO Monday (YYYY-MM-DD, UTC) of the week being set. */
  readonly weekStart: string;
  /** Active Carpe Diem virtues to choose from. */
  readonly virtues: readonly AffirmationGroupEntry[];
  /** The currently-set focus (pre-selects the chip + reason), or null. */
  readonly current: WeeklyFocusEntry | null;
  readonly onSaved: (saved: WeeklyFocusEntry) => void;
}

export function WeeklyFocusPicker({
  clientId,
  weekStart,
  virtues,
  current,
  onSaved,
}: WeeklyFocusPickerProps): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const [selectedVirtueId, setSelectedVirtueId] = useState<string | null>(
    current?.virtueId ?? null,
  );
  const [reason, setReason] = useState(current?.reason ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const byAxis: Record<Axis, AffirmationGroupEntry[]> = {
    energy: [],
    work: [],
    love: [],
  };
  for (const v of virtues) byAxis[v.domain].push(v);

  const onSave = async (): Promise<void> => {
    if (!selectedVirtueId) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await api.setWeeklyFocus(
        accessToken,
        clientId,
        weekStart,
        selectedVirtueId,
        reason,
      );
      onSaved(saved);
    } catch {
      setError(t('weeklyFocus.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="weekly-focus-picker">
      <p className="mt-s1 text-sm text-gray-2 italic">{t('weeklyFocus.prompt')}</p>
      <div className="space-y-s3 mt-s2">
        {AXES.map((axis) => {
          const list = byAxis[axis];
          if (list.length === 0) return null;
          return (
            <fieldset key={axis} data-testid={`weekly-focus-axis-${axis}`}>
              <legend className="form-label">{t(`weeklyFocus.axes.${axis}`)}</legend>
              <div className="flex flex-wrap gap-s2 mt-s1">
                {list.map((v) => {
                  const selected = selectedVirtueId === v.virtueId;
                  return (
                    <button
                      key={v.virtueId}
                      type="button"
                      onClick={() => setSelectedVirtueId(v.virtueId)}
                      data-testid={`weekly-focus-virtue-${v.virtueId}`}
                      data-selected={selected}
                      className={
                        selected
                          ? 'px-s3 py-s1 rounded border border-flame bg-flame text-bone text-sm font-medium'
                          : 'px-s3 py-s1 rounded border border-bone-3 bg-bone-2 text-ink text-sm hover:border-flame-deep'
                      }
                    >
                      {v.virtueName}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          );
        })}
      </div>

      <label htmlFor="weekly-focus-reason" className="form-label mt-s4 block">
        {t('weeklyFocus.reasonLabel')}
      </label>
      <input
        id="weekly-focus-reason"
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('weeklyFocus.reasonPlaceholder')}
        maxLength={500}
        className="input-field mt-s1"
        data-testid="weekly-focus-reason-input"
      />

      {error && (
        <p role="alert" className="mt-s2 text-sm text-flame-deep" data-testid="weekly-focus-error">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={() => void onSave()}
        disabled={saving || !selectedVirtueId}
        className="btn-primary w-full mt-s4"
        data-testid="weekly-focus-save"
      >
        {saving ? t('weeklyFocus.saving') : t('weeklyFocus.save')}
      </button>
    </div>
  );
}
